import { addMapImage } from './lotc/mapboxImageLoad';
import { addMapEurekaImage } from './eureka/mapboxImageLoad';
import { addMapRWCImage } from './rwc/mapboxImageLoad';
import { useEffect, useState } from 'react';
import { addMapSSCImage } from './ssc/mapboxImageLoad';
import { addMapACHImage } from './ach/mapboxImageLoad';
import { addMapJJCImage } from './jjc/mapboxImageLoad';

import { S3_FLOORPLAN_BASE } from 'common/common';
import {
  bankDataForTapFilter,
  bankIdsFileName,
  hasSessionBankIdsMiss,
  isBankTapFilterEnabled,
  isValidBankIdsGeoJson,
  markSessionBankIdsMiss,
  resolveCasinoFloorPlanFiles,
} from 'lib/floorPlanBankTap';

const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000;

type GeoJsonData = unknown;
export const S3_BASE = S3_FLOORPLAN_BASE;

/** Render-path file map (walls/labels/banks). Unknown ids still fall back to LOTC for display only. */
const CASINO_FILES: Record<number, { casinoName: string; files: [string, string, string] }> = {
  1: { casinoName: 'lotc', files: ['lott-walls.json', 'lott-labels.json', 'lott-bank-ids.json'] },
  2: {
    casinoName: 'eureka',
    files: ['eureka-walls.json', 'eureka-labels.json', 'eureka-bank-ids.json'],
  },
  3: { casinoName: 'rwc', files: ['rwc-walls.json', 'rwc-labels.json', 'rwc-bank-ids.json'] },
  22: { casinoName: 'ssc', files: ['ssc-walls.json', 'ssc-labels.json', 'ssc-bank-ids.json'] },
  23: { casinoName: 'ach', files: ['ach-walls.json', 'ach-labels.json', 'ach-bank-ids.json'] },
  24: { casinoName: 'jjc', files: ['jjc-walls.json', 'jjc-labels.json', 'jjc-bank-ids.json'] },
};

export const hasCasinoS3Files = (casinoId: number): boolean => casinoId in CASINO_FILES;

const buildCacheKey = (casinoName: string, fileName: string, invalidateKey?: string | number) =>
  `${casinoName}_${fileName}_${invalidateKey ?? ''}`;

const safeGetLocal = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSetLocal = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore storage errors
  }
};

const fetchGeoJSON = async (
  casinoName: string,
  fileName: string,
  opts?: { quietMiss?: boolean }
): Promise<GeoJsonData | null> => {
  const url = `${S3_BASE}/${casinoName}/${fileName}`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${fileName}: ${res.statusText}`);
    return await res.json();
  } catch (e) {
    if (opts?.quietMiss) {
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug(`[floorplan] bank-ids unavailable: ${casinoName}/${fileName}`);
      }
    } else {
      console.warn('fetchGeoJSON error', e);
    }
    return null;
  }
};

const getCachedData = async (
  casinoName: string,
  fileName: string,
  invalidateKey?: string | number,
  opts?: { quietMiss?: boolean }
): Promise<GeoJsonData | null> => {
  const cacheKey = buildCacheKey(casinoName, fileName, invalidateKey);
  const raw = safeGetLocal(cacheKey);

  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { data: GeoJsonData; timestamp: number };
      if (Date.now() - parsed.timestamp < CACHE_EXPIRATION_MS) return parsed.data;
    } catch (e) {
      // corrupted cache, fallthrough to fetch
    }
  }

  const data = await fetchGeoJSON(casinoName, fileName, opts);
  if (data) safeSetLocal(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
  return data;
};

/**
 * Fetch bank-ids for the render path, with session miss-cache when this is the
 * casino's own bank file (so 404s are not refetched every mount).
 */
async function loadBankIdsForRender(
  casinoId: number,
  casinoName: string,
  bankFile: string,
  invalidateKey?: string | number
): Promise<GeoJsonData | null> {
  const own = resolveCasinoFloorPlanFiles(casinoId);
  const isOwnBankFile =
    !!own && own.casinoName === casinoName && bankIdsFileName(own) === bankFile;

  if (isOwnBankFile && hasSessionBankIdsMiss(casinoName, bankFile)) {
    return null;
  }

  const data = await getCachedData(casinoName, bankFile, invalidateKey, {
    quietMiss: isOwnBankFile,
  });

  if (isOwnBankFile && !isValidBankIdsGeoJson(data)) {
    markSessionBankIdsMiss(casinoName, bankFile);
  }
  return data;
}

const useMapData = (casinoId: number, invalidateKey?: string | number) => {
  const [wallMapData, setWallMapData] = useState<GeoJsonData | null>(null);
  const [labelMapData, setLabelMapData] = useState<GeoJsonData | null>(null);
  const [bankIdMapData, setBankIdMapData] = useState<GeoJsonData | null>(null);
  const [slotsMapData, setSlotsMapData] = useState<GeoJsonData | null>(null);
  /** True only when this casino's own *-bank-ids.json loaded (enables tap-to-filter). */
  const [ownBankIdsLoaded, setOwnBankIdsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Render path: keep existing LOTC fallback for walls/labels/bank *display*.
    const getFilesForCasino = () => CASINO_FILES[casinoId] ?? CASINO_FILES[1];

    const loadData = async () => {
      const { casinoName, files } = getFilesForCasino();
      const [wallsFile, labelsFile, bankFile] = files;
      let slotsMapData;

      const [walls, labels, banks] = await Promise.all([
        getCachedData(casinoName, wallsFile, invalidateKey),
        getCachedData(casinoName, labelsFile, invalidateKey),
        loadBankIdsForRender(casinoId, casinoName, bankFile, invalidateKey),
      ]);

      const tapEnabled = isBankTapFilterEnabled({
        casinoId,
        bankIdMapData: bankDataForTapFilter(casinoId, casinoName, banks),
      });

      if (!cancelled) {
        setWallMapData(walls ?? null);
        setLabelMapData(labels ?? null);
        setBankIdMapData(banks ?? null);
        setOwnBankIdsLoaded(tapEnabled);
        if (slotsMapData) {
          setSlotsMapData(slotsMapData);
        }
      }
    };

    setOwnBankIdsLoaded(false);
    void loadData();

    const onTokenRefreshed = async () => {
      const { casinoName, files } = getFilesForCasino();
      const [wallsFile, labelsFile, bankFile] = files;

      try {
        const [walls, labels, banks] = await Promise.all([
          fetchGeoJSON(casinoName, wallsFile),
          fetchGeoJSON(casinoName, labelsFile),
          loadBankIdsForRender(casinoId, casinoName, bankFile, invalidateKey),
        ]);

        const tapEnabled = isBankTapFilterEnabled({
          casinoId,
          bankIdMapData: bankDataForTapFilter(casinoId, casinoName, banks),
        });

        if (!cancelled) {
          setWallMapData(walls ?? null);
          setLabelMapData(labels ?? null);
          setBankIdMapData(banks ?? null);
          setOwnBankIdsLoaded(tapEnabled);
        }

        try {
          const setIf = (f: string, d: GeoJsonData | null) => {
            if (!d) return;
            safeSetLocal(
              buildCacheKey(casinoName, f, invalidateKey),
              JSON.stringify({ data: d, timestamp: Date.now() })
            );
          };
          setIf(wallsFile, walls);
          setIf(labelsFile, labels);
          setIf(bankFile, banks);
        } catch (e) {
          // ignore
        }
      } catch (err) {
        console.warn('Failed to refresh geojson on token refresh', err);
      }
    };

    window.addEventListener('TokenRefreshed', onTokenRefreshed as EventListener);

    return () => {
      cancelled = true;
      try {
        window.removeEventListener('TokenRefreshed', onTokenRefreshed as EventListener);
      } catch (e) {
        // ignore
      }
    };
  }, [casinoId, invalidateKey]);

  return {
    wallMapData,
    labelMapData,
    bankIdMapData,
    /** When false: do not register bank tap-to-filter (missing/unmapped own bank-ids). */
    ownBankIdsLoaded,
    addMapImage,
    addMapRWCImage,
    addMapEurekaImage,
    addMapSSCImage,
    addMapACHImage,
    addMapJJCImage,
    slotsMapData,
  };
};
export default useMapData;
